/**
 * What's changed, and taking a copy — the two reads behind the history screen (issue #304).
 *
 * **Two endpoints and no writes at all.** `GET /activity` is #299's paged read of AP16;
 * `GET /archive` is #304's whole-partition read, the one ADR 0020 decision 1 named as its
 * re-visit trigger. Reading a family's own record is not a write, so nothing here proposes and
 * nothing here confirms — this is the only screen in the panel with no `POST` behind it.
 *
 * **The response types are declared here rather than imported**, which is ADR 0021 decision 6:
 * the shared contract package waits until mobile is a real second caller. They are a *subset*
 * of what the server discloses, deliberately — a field the screen does not render is a field
 * this file should not claim to understand.
 */

import { type AppApi, appFetch } from "./client";

/** One entry in the record of changes, as `disclosure.ts` shows it to the family. */
export interface ActivityEntry {
  readonly actionId: string;
  /** Who did it, or `null` for somebody who has since left — never a raw ULID (#117). */
  readonly by: string | null;
  readonly tool: string;
  /** What happened, in the family's own terms. The row's sentence. */
  readonly summary: string;
  /** Where the write landed, already in the family's words (`WRITTEN_VIA`). */
  readonly via: string;
  /** Where they were *asked*, and only when it differs from {@link via} (#314). */
  readonly askedVia: string | null;
  /** Kept past the 90-day sweep — a membership or role change (ADR 0003 decision 10). */
  readonly keptForGood: boolean;
  /** #114 emptied this entry because the fact it quoted was deleted. */
  readonly redacted: boolean;
  /** When it was emptied — the deletion's date, not this entry's. `null` when it was not. */
  readonly redactedAt: string | null;
  readonly at: string;
}

export interface ActivityPage {
  readonly records: readonly ActivityEntry[];
  /** Hand back as `?after=`. `null` means that was the last page. */
  readonly nextCursor: string | null;
  /**
   * The instant before which only permanent entries survive — the boundary the screen draws.
   *
   * **From the server, never computed here.** Two things would go wrong with a local copy: the
   * number is `EPHEMERAL_ACTION_RETENTION_DAYS` and a bundle holding its own would keep drawing
   * the old line after it moved, with nothing failing to build; and the arithmetic would run on
   * a browser clock, which is wrong on more machines than anybody expects.
   */
  readonly everythingKeptSince: string;
}

function familyPath(familyId: string, tail: string): string {
  return `/app/families/${encodeURIComponent(familyId)}${tail}`;
}

export function fetchActivityPage(
  api: AppApi,
  familyId: string,
  after: string | null,
): Promise<ActivityPage> {
  const query = after === null ? "" : `?after=${encodeURIComponent(after)}`;
  return appFetch<ActivityPage>(api, familyPath(familyId, `/activity${query}`));
}

/** One projected record inside a section of the archive. */
export interface ArchiveEntry {
  readonly id: string;
  /**
   * The record's fields, as the archive projects them.
   *
   * **Open on purpose, and the one place in this app that is.** The document renderer walks
   * these rather than naming them, which is what makes the downloaded file a total function of
   * the projection: a field added server-side appears in the file without an edit here, and a
   * field removed disappears from it. Naming them would be a second copy of nine projections,
   * kept in step by hand, on a pair of surfaces whose whole claim is completeness.
   */
  readonly fields: Readonly<Record<string, unknown>>;
}

export interface ArchiveSection {
  /** The wire name — `backlog`, `history` — used as a key and never shown. */
  readonly section: string;
  /** The family-facing heading, which is what the file prints. */
  readonly label: string;
  readonly entries: readonly ArchiveEntry[];
}

export interface FamilyArchive {
  /** When the server read the partition. The file states this; the browser must not. */
  readonly snapshotAt: string;
  readonly sections: readonly ArchiveSection[];
  readonly totalRecords: number;
  /**
   * Rows the table held that this document does not have.
   *
   * Every completeness claim on the screen and in the file hangs off this being zero — the
   * same rule `shapeExport`'s `message` follows for a host.
   */
  readonly unreadable: number;
  /** What the archive cannot contain, in the server's own sentences. */
  readonly doesNotInclude: readonly string[];
}

/**
 * The whole archive, in one request.
 *
 * **No cursor, and that absence is the feature.** ADR 0020 had to define *portable* as a
 * document delivered in numbered parts because a chat host's context window made anything else
 * impossible; a browser has no such limit, so the file is assembled from one read. Walking a
 * cursor here would put the completeness claim in the weakest possible place — a loop that
 * stopped early would still produce a file saying *"that's the lot"*.
 */
export function fetchArchive(api: AppApi, familyId: string): Promise<FamilyArchive> {
  return appFetch<FamilyArchive>(api, familyPath(familyId, "/archive"));
}
